import { gql, useMutation } from "@apollo/client";
import "@fullcalendar/core/main.css";
import "@fullcalendar/daygrid/main.css";
import "@fullcalendar/timegrid/main.css";
import "primeflex/primeflex.css";
import "primeicons/primeicons.css";
import { InputText } from "primereact/inputtext";
import { Messages } from "primereact/messages";
import { Panel } from "primereact/panel";
import "primereact/resources/primereact.min.css";
import "primereact/resources/themes/saga-blue/theme.css";
import "prismjs/themes/prism-coy.css";
import React, { useEffect, useRef, useState } from "react";
import "../../App.scss";
import "../../layout/flags/flags.css";
import "../../layout/layout.scss";
import { useDispatch, useSelector } from "react-redux";
import { setNavigationMode } from "../../reducers/userLoginReducer";
import { addToPresetBuildList } from "../../reducers/buildReducer";
import { pushMessage } from "../../reducers/messagingReducer";
import SectionHeader from "../helper/SectionHeader";
import LayoutWrapper from "../LayoutWrapper";
import { registerScreenChanges } from "../../reducers/screenChangesReducer";
import {InputSwitch} from "primereact/inputswitch";

const PRESET_SETTINGS_MUTATION = gql`
    mutation callDomainEditMutation($id: String, $cid: String) {
        presetAllInfoMutation(input: { id: $id, cid: $cid }) {
            presetInfo
        }
    }
`;

const PRESETSETTINGS_UPDATE_MUTATION = gql`
    mutation callAdPositionUpdateMutation($id: String, $presetName: String, $bannerFloorsPrice: String, $morning_6_12: String, $afternoon_12_6: String, $evening_6_12: String, $midnight_12_6: String, $enableDynamicFloors: Boolean, $dynamicFloorsPercentage: String) {
        updatePresetSettings(input: {
            id: $id,
            presetName: $presetName,
            bannerFloorsPrice: $bannerFloorsPrice,
            morning_6_12: $morning_6_12,
            afternoon_12_6: $afternoon_12_6,
            evening_6_12: $evening_6_12,
            midnight_12_6: $midnight_12_6,
            enableDynamicFloors: $enableDynamicFloors,
            dynamicFloorsPercentage: $dynamicFloorsPercentage
        }) { networkId }
    }
`;

const PresetSettings = () => {
    const [mutationDataCompleted, setMutationDataCompleted] = useState(false);
    const [inputList, setInputList] = useState([{ display_name: "", slot_name: "", slot_name: "", desktop_viewport: "", desktop_adsize: "", tablet_viewport: "", tablet_adsize: "", mobile_viewport: "", mobile_adsize: "" }]);
    const [bannerFloorsPrice, setBannerFloorsPrice] = useState(null);
    const [bannerFloorsPriceTimeSlots, setBannerFloorsPriceTimeSlots] = useState({});
    const [presetName, setPresetName] = useState(null);
    const [presetList, setPresetList] = useState(null);
    const [presetSettingsInfo, setPresetSettingsInfo] = useState({});
    const [originalPresetName, setOriginalPresetName] = useState(null);
    const messages = useRef(null);
    const dispatch = useDispatch();
    const { currentPreset, currentCustomer } = useSelector((state) => {
        return state.userInformation;
    });

    const [DOMAIN_EDIT_MUTATION_CONTAINER] = useMutation(PRESET_SETTINGS_MUTATION, {
        onCompleted(data) {
            confirm(data);
        },
    });

    const [PRESET_UPDATE_MUTATION_CONTAINER] = useMutation(PRESETSETTINGS_UPDATE_MUTATION, {
        onCompleted() {
            dispatch(addToPresetBuildList(currentPreset));
            dispatch(pushMessage("Preset settings", "Settings were updated"));
        },
    });

    useEffect(() => {
        if (currentCustomer && currentPreset) {
            dispatch(setNavigationMode("preset"));
            DOMAIN_EDIT_MUTATION_CONTAINER({
                variables: { id: currentPreset, cid: currentCustomer },
            });
        }
    }, [currentPreset, currentCustomer]);

    const confirm = (data) => {
        let temp_data = JSON.parse(data.presetAllInfoMutation.presetInfo);
        setPresetSettingsInfo(temp_data);
        setBannerFloorsPrice(temp_data.banner_floors_price);
        setBannerFloorsPriceTimeSlots({
            morning_6_12: temp_data.morning_6_12,
            afternoon_12_6: temp_data.afternoon_12_6,
            evening_6_12: temp_data.evening_6_12,
            midnight_12_6: temp_data.midnight_12_6,
        });
        setPresetName(temp_data.adv_preset_name);
        setOriginalPresetName(temp_data.adv_preset_name);
        setMutationDataCompleted(true);
    };

    const handleChange = (param) => {
        let name = param.target.name,
            value = param.target.value;

        let temp = {};

        if (
            name === "morning_6_12" ||
            name === "afternoon_12_6" ||
            name === "evening_6_12" ||
            name === "midnight_12_6"
        ) {
            setBannerFloorsPriceTimeSlots(state=>{
                return {
                    ...state,
                    [name]: value,
                };
            });
        }

        if (name === "banner_floors_price") {
            setBannerFloorsPrice(value);
        } else if(name === "adv_preset_name") {
            setPresetName(value);
        } else {
            temp = Object.assign(temp, presetSettingsInfo);
            temp[name] = value;

            setPresetSettingsInfo(temp);
        }

        dispatch(registerScreenChanges("Preset Settings"));
    };

    const savePresetSettings = async () => {
        let saveData = true;

        if (bannerFloorsPrice && isNaN(parseFloat(bannerFloorsPrice))) {
            dispatch(pushMessage("Preset settings", "Banner Floors Price is invalid", "error"));
            saveData = false;
        }

        if (presetName !== originalPresetName) {
            presetList.forEach((list) => {
                if (list.title === presetName) {
                    dispatch(pushMessage("Preset settings", `${presetName} already exist`, "error"));
                    saveData = false;
                } else {
                    if (presetName === "" || presetName === null) {
                        dispatch(pushMessage("Preset settings", "Preset name cannot be empty", "error"));
                        saveData = false;
                    }
                }
            });
        }

        if (!saveData) throw new Error("Error saving preset data");
        if (presetName !== originalPresetName) {
            presetList.forEach((list) => {
                if (list.id === currentPreset) {
                    list.title = presetName;
                }
            });
        }

        let temp = Object.assign({});
        inputList.forEach((list) => {
            if (list.slot_name === "") {
            } else {
                temp[list.slot_name] = Object.assign({});
                if (list.deskAdsize !== null && list.deskAdsize !== "" && list.deskViewPort !== null && list.deskViewPort !== "") {
                    temp[list.slot_name] = {
                        desk: {
                            viewport: list.deskViewPort,
                            adsize: list.deskAdsize,
                        },
                    };
                }
                if (list.tabAdsize !== null && list.tabAdsize !== "" && list.tabViewPort !== null && list.tabViewPort !== "") {
                    temp[list.slot_name] = Object.assign(temp[list.slot_name], {
                        tab: {
                            viewport: list.tabViewPort,
                            adsize: list.tabAdsize,
                        },
                    });
                }
                if (list.mobAdsize !== null && list.mobAdsize !== "" && (list.mobViewPort !== null) & (list.mobViewPort !== "")) {
                    temp[list.slot_name] = Object.assign(temp[list.slot_name], {
                        mob: {
                            viewport: list.mobViewPort,
                            adsize: list.mobAdsize,
                        },
                    });
                }
                if (list.display_name !== "") {
                    temp[list.slot_name] = Object.assign(temp[list.slot_name], { display_name: list.display_name });
                }
                if (list.slot_name !== "") {
                    temp[list.slot_name] = Object.assign(temp[list.slot_name], { slot_name: list.slot_name });
                }
            }
        });
        return PRESET_UPDATE_MUTATION_CONTAINER({
            variables: {
                id: currentPreset,
                presetName: presetName,
                bannerFloorsPrice: bannerFloorsPrice,
                morning_6_12: bannerFloorsPriceTimeSlots.morning_6_12 ?? 0,
                afternoon_12_6: bannerFloorsPriceTimeSlots.afternoon_12_6 ?? 0,
                evening_6_12: bannerFloorsPriceTimeSlots.evening_6_12 ?? 0,
                midnight_12_6: bannerFloorsPriceTimeSlots.midnight_12_6 ?? 0,
                enableDynamicFloors: presetSettingsInfo.enable_dynamic_floors,
                dynamicFloorsPercentage: presetSettingsInfo.dynamic_floors_percentage,
            },
        });
    };

    return (
        <LayoutWrapper model="preset" saveCallback={savePresetSettings}>
            {mutationDataCompleted && (
                <div className="p-grid">
                    <div className="p-col-12 p-md-10 p-lg-10 contact-form">
                        <Messages ref={messages} />
                        <div className="p-panel p-component">
                            <SectionHeader screenName="Preset settings">
                                <div className="p-text-bold">Preset settings</div>
                            </SectionHeader>
                        </div>
                        <Panel>
                            <div className="p-fluid contact-form">
                                <div className="p-grid">
                                    <div className="p-col-12 p-sm-12 p-md-3 p-lg-3 p-mt-2 small-margin-top-0 small-padding-bottom-0">
                                        <label htmlFor="preset_name">Preset Title</label>
                                    </div>
                                    <div className="p-col-12 p-sm-12 p-md-9 p-lg-9">
                                        <InputText id="preset_name" name="preset_name" value={presetName} onChange={handleChange}/>
                                    </div>
                                </div>

                                <div className="p-grid">
                                    <div className="p-col-12 p-sm-12 p-md-3 p-lg-3 p-mt-2 small-margin-top-0 small-padding-bottom-0">
                                        <label htmlFor="banner_floors_price">Global Price Floors</label>
                                    </div>
                                    <div className="p-col-12 p-sm-12 p-md-9 p-lg-9">
                                        <InputText id="banner_floors_price" name="banner_floors_price" value={bannerFloorsPrice} onChange={handleChange}/>
                                    </div>
                                </div>

                                <div className="p-grid">
                                    <div className="p-col-12 p-sm-12 p-md-3 p-lg-3 p-mt-2 small-margin-top-0 small-padding-bottom-0">
                                        <label htmlFor="banner_floors_price">Price Floors Based on Time</label>
                                    </div>
                                    <div className="p-col-12 p-sm-12 p-md-9 p-lg-9">
                                        <div className="adapter-parameters adpos" style={{paddingTop: '5px', padding: 0}}>
                                            <div className="sizing-row all-sizes"></div>
                                            <div className="controls-wrapper all-sizes" style={{width: "100%"}}>
                                                <div className="adapter-config">

                                                    <div className="device desktop price-floors-heading">
                                                        <small className=""><b>Morning</b> ( 6AM-12PM )</small>
                                                    </div>
                                                    <div className="device desktop price-floors-heading">
                                                        <small className=""><b>Afternoon</b> ( 12PM-6PM )</small>
                                                    </div>
                                                    <div className="device desktop price-floors-heading">
                                                        <small className=""><b>Evening</b> ( 6PM-12AM )</small>
                                                    </div>
                                                    <div className="device desktop price-floors-heading">
                                                        <small className=""><b>Midnight</b> ( 12AM-6PM )</small>
                                                    </div>

                                                </div>


                                                <div className="values display-ad">
                                                    <div className="adapter-parameter-values price-floors-inputs" data-index="0" style={{padding: "1rem", width: "25%"}}>

                                                        <div className="param-input adpos">
                                                            <InputText id="morning_6_12" name="morning_6_12" value={bannerFloorsPriceTimeSlots.morning_6_12} onChange={handleChange}/>
                                                        </div>
                                                    </div>
                                                    <div className="adapter-parameter-values price-floors-inputs" data-index="1">

                                                        <div className="param-input adpos">
                                                            <InputText id="afternoon_12_6" name="afternoon_12_6" value={bannerFloorsPriceTimeSlots.afternoon_12_6} onChange={handleChange}/>
                                                        </div>
                                                    </div>

                                                    <div className="adapter-parameter-values price-floors-inputs" data-index="2">

                                                        <div className="param-input adpos">
                                                            <InputText id="evening_6_12" name="evening_6_12" value={bannerFloorsPriceTimeSlots.evening_6_12} onChange={handleChange}/>
                                                        </div>
                                                    </div>

                                                    <div className="adapter-parameter-values price-floors-inputs" data-index="2">

                                                        <div className="param-input adpos">
                                                            <InputText id="midnight_12_6" name="midnight_12_6" value={bannerFloorsPriceTimeSlots.midnight_12_6} onChange={handleChange}/>
                                                        </div>
                                                    </div>

                                                </div>

                                            </div>
                                        </div>
                                    </div>
                                </div>



                                {/*<div className="p-grid">
                                    <div className="p-col-12 p-sm-12 p-md-3 p-lg-3 p-mt-2 small-margin-top-0 small-padding-bottom-0">
                                        <label htmlFor="morning_6_12">Price Floors Morning</label>
                                    </div>
                                    <div className="p-col-12 p-sm-12 p-md-9 p-lg-9">
                                        <InputText id="morning_6_12" name="morning_6_12" value={bannerFloorsPriceTimeSlots.morning_6_12} onChange={handleChange}/>
                                    </div>
                                </div>
                                <div className="p-grid">
                                    <div className="p-col-12 p-sm-12 p-md-3 p-lg-3 p-mt-2 small-margin-top-0 small-padding-bottom-0">
                                        <label htmlFor="afternoon_12_6">Price Floors Afternoon</label>
                                    </div>
                                    <div className="p-col-12 p-sm-12 p-md-9 p-lg-9">
                                        <InputText id="afternoon_12_6" name="afternoon_12_6" value={bannerFloorsPriceTimeSlots.afternoon_12_6} onChange={handleChange}/>
                                    </div>
                                </div>
                                <div className="p-grid">
                                    <div className="p-col-12 p-sm-12 p-md-3 p-lg-3 p-mt-2 small-margin-top-0 small-padding-bottom-0">
                                        <label htmlFor="evening_6_12">Price Floors Evening</label>
                                    </div>
                                    <div className="p-col-12 p-sm-12 p-md-9 p-lg-9">
                                        <InputText id="evening_6_12" name="evening_6_12" value={bannerFloorsPriceTimeSlots.evening_6_12} onChange={handleChange}/>
                                    </div>
                                </div>
                                <div className="p-grid">
                                    <div className="p-col-12 p-sm-12 p-md-3 p-lg-3 p-mt-2 small-margin-top-0 small-padding-bottom-0">
                                        <label htmlFor="midnight_12_6">Price Floors Midnight</label>
                                    </div>
                                    <div className="p-col-12 p-sm-12 p-md-9 p-lg-9">
                                        <InputText id="midnight_12_6" name="midnight_12_6" value={bannerFloorsPriceTimeSlots.midnight_12_6} onChange={handleChange}/>
                                    </div>
                                </div>*/}

                                <div className="p-grid small-margin-top-10">
                                    <div className="p-col-3 p-sm-none small-hide">
                                        <label className="adpositiontooltip after" data-pr-tooltip="Check to enable lazyload for this Publisher." htmlFor="enable_lazyload">
                                            Enable Dynamic Floors
                                        </label>
                                    </div>
                                    <div className="p-col-9 p-fluid">
                                        <div className="p-d-flex p-ai-center p-mb-2">
                                            <InputSwitch name="enable_dynamic_floors" checked={presetSettingsInfo.enable_dynamic_floors} onChange={handleChange}/>
                                            <label htmlFor="enable_dynamic_floors" className="adpositiontooltip after p-ml-2 p-d-none small-show" data-pr-tooltip="">
                                                Enable Dynamic Floors
                                            </label>
                                        </div>
                                    </div>
                                </div>

                                <div className="p-grid">
                                    <div className="p-col-12 p-sm-12 p-md-3 p-lg-3 p-mt-2 small-margin-top-0 small-padding-bottom-0">
                                        <label htmlFor="dynamic_floors_percentage">Dynamic Floors Percentage</label>
                                    </div>
                                    <div className="p-col-12 p-sm-12 p-md-9 p-lg-9">
                                        <InputText id="dynamic_floors_percentage" name="dynamic_floors_percentage" value={presetSettingsInfo.dynamic_floors_percentage} onChange={handleChange}/>
                                    </div>
                                </div>
                            </div>
                        </Panel>
                    </div>
                </div>
            )}
        </LayoutWrapper>
    );
};

export default PresetSettings;
